import React, { useState, useEffect, useCallback } from 'react';
import { MemberService } from '../../services/memberService';
import { useMeetings } from '../../hooks/useMeetings';
import { useAuth } from '../../contexts/AuthContext';
import LoadingState from '../shared/LoadingState';
import { PDFReportGenerator, DateDetailMode } from '../../utils/pdfReportGenerator';
import { Member, MemberFormData } from '../../types/database';
import { Plus, Search, Edit, Trash2, Phone, Tag, MessageSquare, FileText, CheckCircle2, XCircle, UserPlus, Sparkles } from 'lucide-react';

export default function MemberManagement() {
  const { user } = useAuth();
  const { meetingTypes } = useMeetings();
  const [members, setMembers] = useState<any[]>([]);
  const [filteredMembers, setFilteredMembers] = useState<any[]>([]);
  const [selectedMeetingType, setSelectedMeetingType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dateDetailMode, setDateDetailMode] = useState<DateDetailMode>('none');
  const [attendanceFilter, setAttendanceFilter] = useState<'all' | 'high' | 'low' | 'absent'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadMembers = useCallback(async () => {
    if (!user?.id) return;

    setLoading(true);
    setError('');
    const { data, error } = await MemberService.getConvenorMemberAttendanceSummary(
      user.id,
      selectedMeetingType || undefined,
      { startDate, endDate }
    );
    if (data) {
      setMembers(data);
    } else if (error) {
      setError(error.message);
    }
    setLoading(false);
  }, [user?.id, selectedMeetingType, startDate, endDate]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  const filterMembers = useCallback(() => {
    let result = [...members];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(m =>
        m.name.toLowerCase().includes(q) ||
        m.phone.includes(q) ||
        (m.tags && m.tags.toLowerCase().includes(q))
      );
    }

    if (attendanceFilter === 'high') {
      result = result.filter(m => (m.stats?.percentage || 0) >= 80);
    } else if (attendanceFilter === 'low') {
      result = result.filter(m => (m.stats?.percentage || 0) < 50 && (m.stats?.totalMeetings || 0) > 0);
    } else if (attendanceFilter === 'absent') {
      result = result.filter(m => (m.stats?.present || 0) === 0);
    }

    setFilteredMembers(result);
  }, [members, searchQuery, attendanceFilter]);

  useEffect(() => {
    filterMembers();
  }, [filterMembers]);

  const handleAddMember = async (formData: MemberFormData) => {
    setLoading(true);
    const { data, error } = await MemberService.addMember(formData);
    
    if (data) {
      await loadMembers();
      setShowAddModal(false);
    } else if (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  const handleEditMember = async (memberId: string, formData: Partial<MemberFormData>) => {
    setLoading(true);
    const { data, error } = await MemberService.updateMember(memberId, formData);
    
    if (data) {
      await loadMembers();
      setEditingMember(null);
    } else if (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  const handleDeleteMember = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to delete this member?')) return;

    setLoading(true);
    const { error } = await MemberService.deleteMember(memberId);
    
    if (error) {
      setError(error.message);
    } else {
      await loadMembers();
    }
    setLoading(false);
  };

  const exportMemberWisePDF = () => {
    if (filteredMembers.length === 0) return;

    const reportRows = filteredMembers.map(m => {
      const stats = m.stats || { present: 0, absent: 0, totalMeetings: 0, percentage: 0 };
      return {
        name: m.name,
        phone: m.phone,
        meetingType: m.meeting_types?.name || 'Assigned',
        total: stats.totalMeetings,
        present: stats.present,
        absent: stats.absent || (stats.totalMeetings - stats.present),
        percentage: stats.percentage,
        presentDates: stats.presentDates,
        absentDates: stats.absentDates
      };
    });

    const filterSummary: string[] = [];
    if (selectedMeetingType) {
      const typeObj = meetingTypes.find(t => t.id === selectedMeetingType);
      if (typeObj) filterSummary.push(`Meeting Type: ${typeObj.name} (${typeObj.level})`);
    }

    let dateRangeText = '';
    if (startDate && endDate) dateRangeText = `${startDate} to ${endDate}`;
    else if (startDate) dateRangeText = `From ${startDate}`;
    else if (endDate) dateRangeText = `Up to ${endDate}`;

    PDFReportGenerator.generateMemberWisePDF(reportRows, {
      reportTitle: 'Member Attendance Report',
      filterSummary,
      dateRangeText,
      dateDetailMode
    });
  };

  const shareWhatsAppMemberSummary = () => {
    if (filteredMembers.length === 0) return;

    let text = `👥 *MEMBER ATTENDANCE SUMMARY*\n`;
    text += `━━━━━━━━━━━━━━━━━━━━\n`;
    if (selectedMeetingType) {
      const typeObj = meetingTypes.find(t => t.id === selectedMeetingType);
      if (typeObj) text += `📌 *Meeting Type:* ${typeObj.name} (${typeObj.level})\n`;
    }
    if (startDate || endDate) {
      text += `📅 *Date Range:* ${startDate || 'Start'} to ${endDate || 'Present'}\n`;
    }
    if (dateDetailMode !== 'none') {
      const modeLabel = dateDetailMode === 'present' ? 'Dates of Attendance' : dateDetailMode === 'absent' ? 'Dates of Absence' : 'Present & Absent Dates';
      text += `📌 *View Mode:* ${modeLabel}\n`;
    }
    text += `📊 *Total Members:* ${filteredMembers.length}\n\n`;

    filteredMembers.forEach((m, idx) => {
      const stats = m.stats || { present: 0, totalMeetings: 0, percentage: 0, presentDates: [], absentDates: [] };
      const percentStr = stats.totalMeetings > 0 ? `${stats.percentage.toFixed(1)}%` : 'N/A';
      const badge = stats.percentage >= 75 ? '🟢' : stats.percentage >= 50 ? '🟡' : '🔴';
      text += `${idx + 1}. *${m.name}* (${m.phone})\n`;
      text += `   ↳ ${badge} Attended: ${stats.present}/${stats.totalMeetings} meetings (${percentStr})\n`;
      if ((dateDetailMode === 'present' || dateDetailMode === 'both') && stats.presentDates?.length > 0) {
        text += `   ↳ ✅ Present Dates: ${stats.presentDates.join(', ')}\n`;
      }
      if ((dateDetailMode === 'absent' || dateDetailMode === 'both') && stats.absentDates?.length > 0) {
        text += `   ↳ ❌ Absent Dates: ${stats.absentDates.join(', ')}\n`;
      }
    });

    text += `\n━━━━━━━━━━━━━━━━━━━━\n`;
    text += `*State Meeting Tracker | SSF Kerala*`;

    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const totalMembersCount = members.length;

  return (
    <div className="space-y-6">
      {/* Header & Controls */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Member Attendance Summary</h2>
            <p className="text-xs sm:text-sm text-gray-500">Track member attendance rates and contact details across assigned meeting types.</p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={exportMemberWisePDF}
              disabled={loading || filteredMembers.length === 0}
              className="flex items-center space-x-1.5 px-3 py-2 bg-red-600 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-red-700 transition disabled:opacity-50 shadow-2xs"
            >
              <FileText size={16} />
              <span>Export PDF</span>
            </button>
            <button
              onClick={shareWhatsAppMemberSummary}
              disabled={loading || filteredMembers.length === 0}
              className="flex items-center space-x-1.5 px-3 py-2 bg-green-600 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-green-700 transition disabled:opacity-50 shadow-2xs"
            >
              <MessageSquare size={16} />
              <span>WhatsApp Summary</span>
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center space-x-1.5 px-3.5 py-2 bg-indigo-600 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-indigo-700 transition shadow-2xs"
              title="Select or import existing members from other meeting types"
            >
              <UserPlus size={16} />
              <span>Import / Select Members</span>
            </button>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center space-x-1.5 px-4 py-2 bg-blue-600 text-white text-xs sm:text-sm font-semibold rounded-xl hover:bg-blue-700 transition shadow-2xs"
            >
              <Plus size={16} />
              <span>Add Member</span>
            </button>
          </div>
        </div>

        {/* Filters & Search */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 pt-2">
          {/* Meeting Type Selection */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Meeting Type
            </label>
            <select
              value={selectedMeetingType}
              onChange={(e) => setSelectedMeetingType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
            >
              <option value="">All Assigned Types</option>
              {meetingTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name} - {type.level}
                </option>
              ))}
            </select>
          </div>

          {/* Start Date Picker */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
            />
          </div>

          {/* End Date Picker */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
            />
          </div>

          {/* Search Bar */}
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">
              Search Members
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Search name, phone, tags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
              />
            </div>
          </div>
        </div>

        {/* Attendance Rate & Date Details Selection Controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 pt-2 border-t border-gray-100">
          {/* Date Details Option */}
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-xs font-bold text-gray-700 mr-1">Date Details Option:</span>
            <button
              onClick={() => setDateDetailMode('none')}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                dateDetailMode === 'none'
                  ? 'bg-blue-600 text-white font-semibold'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Summary
            </button>
            <button
              onClick={() => setDateDetailMode('present')}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition flex items-center space-x-1 ${
                dateDetailMode === 'present'
                  ? 'bg-green-600 text-white font-semibold'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <CheckCircle2 size={12} />
              <span>Attendance Dates</span>
            </button>
            <button
              onClick={() => setDateDetailMode('absent')}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition flex items-center space-x-1 ${
                dateDetailMode === 'absent'
                  ? 'bg-red-600 text-white font-semibold'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <XCircle size={12} />
              <span>Absentee Dates</span>
            </button>
            <button
              onClick={() => setDateDetailMode('both')}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                dateDetailMode === 'both'
                  ? 'bg-purple-600 text-white font-semibold'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Both
            </button>
          </div>

          {/* Attendance Rate Filter */}
          <div className="flex items-center space-x-2">
            <span className="text-xs font-bold text-gray-700 whitespace-nowrap">Filter Rate:</span>
            <div className="flex rounded-xl bg-gray-100 p-1 text-xs">
              <button
                onClick={() => setAttendanceFilter('all')}
                className={`px-3 py-1 font-medium rounded-lg transition ${
                  attendanceFilter === 'all' ? 'bg-white text-gray-900 shadow-xs font-semibold' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All ({totalMembersCount})
              </button>
              <button
                onClick={() => setAttendanceFilter('high')}
                className={`px-3 py-1 font-medium rounded-lg transition ${
                  attendanceFilter === 'high' ? 'bg-white text-green-700 shadow-xs font-semibold' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                ≥80%
              </button>
              <button
                onClick={() => setAttendanceFilter('low')}
                className={`px-3 py-1 font-medium rounded-lg transition ${
                  attendanceFilter === 'low' ? 'bg-white text-red-700 shadow-xs font-semibold' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                &lt;50%
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
          {error}
        </div>
      )}

      {/* Members Content */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <LoadingState title="Loading Member Attendance Summaries..." minHeight="min-h-[300px]" />
        ) : filteredMembers.length > 0 ? (
          <div>
            {/* Desktop Table View */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-xs sm:text-sm divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Member Name</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Meeting Type</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Phone</th>
                    <th className="px-4 py-3 text-center font-semibold text-gray-700">Attendance Rate</th>
                    {(dateDetailMode === 'present' || dateDetailMode === 'both') && (
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Dates of Attendance</th>
                    )}
                    {(dateDetailMode === 'absent' || dateDetailMode === 'both') && (
                      <th className="px-4 py-3 text-left font-semibold text-gray-700">Dates of Absence</th>
                    )}
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Tags</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {filteredMembers.map((member) => {
                    const stats = member.stats || { present: 0, totalMeetings: 0, percentage: 0, presentDates: [], absentDates: [] };
                    return (
                      <tr key={member.id} className="hover:bg-gray-50 transition">
                        <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                          <div className="flex items-center space-x-3">
                            <div className="h-8 w-8 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold text-xs">
                              {member.name.charAt(0).toUpperCase()}
                            </div>
                            <span>{member.name}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600">
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-800 border border-blue-100">
                            {member.meeting_types?.name || 'Assigned'}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-gray-600 font-mono text-xs">
                          {member.phone}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <span className="font-semibold text-gray-900 text-xs">
                              {stats.present}/{stats.totalMeetings}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                              stats.totalMeetings === 0
                                ? 'bg-gray-100 text-gray-600'
                                : stats.percentage >= 75
                                  ? 'bg-green-100 text-green-800'
                                  : stats.percentage >= 50
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                            }`}>
                              {stats.totalMeetings > 0 ? `${stats.percentage.toFixed(1)}%` : 'No meetings'}
                            </span>
                          </div>
                        </td>
                        {(dateDetailMode === 'present' || dateDetailMode === 'both') && (
                          <td className="px-4 py-3 text-xs text-green-700 max-w-xs">
                            {stats.presentDates && stats.presentDates.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {stats.presentDates.map((d: string, idx: number) => (
                                  <span key={idx} className="bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded text-[11px]">
                                    {d}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">None</span>
                            )}
                          </td>
                        )}
                        {(dateDetailMode === 'absent' || dateDetailMode === 'both') && (
                          <td className="px-4 py-3 text-xs text-red-700 max-w-xs">
                            {stats.absentDates && stats.absentDates.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {stats.absentDates.map((d: string, idx: number) => (
                                  <span key={idx} className="bg-red-50 text-red-700 border border-red-200 px-1.5 py-0.5 rounded text-[11px]">
                                    {d}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">None</span>
                            )}
                          </td>
                        )}
                        <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                          {member.tags ? (
                            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-700">{member.tags}</span>
                          ) : (
                            <span className="text-gray-400 italic">No tags</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-1">
                            <button
                              onClick={() => setEditingMember(member)}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title="Edit Member"
                            >
                              <Edit size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteMember(member.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                              title="Delete Member"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="sm:hidden space-y-3 p-4">
              {filteredMembers.map((member) => {
                const stats = member.stats || { present: 0, totalMeetings: 0, percentage: 0, presentDates: [], absentDates: [] };
                return (
                  <div key={member.id} className="border border-gray-200 rounded-xl p-4 bg-white hover:bg-gray-50 transition space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold text-sm">
                          {member.name.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-bold text-gray-900 leading-tight">{member.name}</div>
                          <div className="text-[11px] text-blue-600 font-semibold mt-0.5">
                            {member.meeting_types?.name || 'Assigned'}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => setEditingMember(member)}
                          className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition min-w-[36px] min-h-[36px] flex items-center justify-center"
                          title="Edit"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteMember(member.id)}
                          className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition min-w-[36px] min-h-[36px] flex items-center justify-center"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-gray-100 text-xs">
                      <div className="bg-gray-50 p-2 rounded-lg">
                        <span className="text-[10px] text-gray-500 block font-medium">ATTENDANCE</span>
                        <span className="font-bold text-gray-900 text-xs">
                          {stats.present} / {stats.totalMeetings} meetings
                        </span>
                      </div>
                      <div className="bg-gray-50 p-2 rounded-lg text-right">
                        <span className="text-[10px] text-gray-500 block font-medium">RATE</span>
                        <span className={`font-bold text-xs px-2 py-0.5 rounded-full inline-block ${
                          stats.totalMeetings === 0
                            ? 'bg-gray-200 text-gray-700'
                            : stats.percentage >= 75
                              ? 'bg-green-100 text-green-800'
                              : stats.percentage >= 50
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                        }`}>
                          {stats.totalMeetings > 0 ? `${stats.percentage.toFixed(1)}%` : 'N/A'}
                        </span>
                      </div>
                    </div>

                    {(dateDetailMode === 'present' || dateDetailMode === 'both') && stats.presentDates?.length > 0 && (
                      <div className="pt-2 border-t border-gray-100">
                        <div className="font-semibold text-green-700 mb-1 flex items-center space-x-1">
                          <CheckCircle2 size={12} />
                          <span>Dates Attended:</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {stats.presentDates.map((d: string, idx: number) => (
                            <span key={idx} className="bg-green-50 text-green-800 border border-green-200 px-1.5 py-0.5 rounded text-[10px]">
                              {d}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {(dateDetailMode === 'absent' || dateDetailMode === 'both') && stats.absentDates?.length > 0 && (
                      <div className="pt-2 border-t border-gray-100">
                        <div className="font-semibold text-red-700 mb-1 flex items-center space-x-1">
                          <XCircle size={12} />
                          <span>Dates Absent:</span>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {stats.absentDates.map((d: string, idx: number) => (
                            <span key={idx} className="bg-red-50 text-red-800 border border-red-200 px-1.5 py-0.5 rounded text-[10px]">
                              {d}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-1 text-xs">
                      <a
                        href={`tel:${member.phone}`}
                        className="inline-flex items-center space-x-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg font-semibold transition"
                      >
                        <Phone size={13} />
                        <span>{member.phone}</span>
                      </a>
                      {member.tags && (
                        <div className="inline-flex items-center text-gray-500 font-medium bg-gray-100 px-2 py-1 rounded-md text-[11px]">
                          <Tag size={12} className="mr-1" />
                          <span>{member.tags}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 text-xs sm:text-sm">
            {searchQuery ? 'No members found matching your search' : 'No members found for the selected filter.'}
          </div>
        )}
      </div>

      {/* Import / Select Members Modal */}
      {showImportModal && user?.id && (
        <ImportMembersModal
          meetingTypes={meetingTypes}
          currentUserId={user.id}
          defaultMeetingTypeId={selectedMeetingType}
          onImportSuccess={loadMembers}
          onClose={() => setShowImportModal(false)}
        />
      )}

      {/* Add Member Modal */}
      {showAddModal && (
        <MemberModal
          title="Add New Member"
          meetingTypes={meetingTypes}
          onSubmit={handleAddMember}
          onClose={() => setShowAddModal(false)}
          loading={loading}
          defaultMeetingTypeId={selectedMeetingType}
        />
      )}

      {/* Edit Member Modal */}
      {editingMember && (
        <MemberModal
          title="Edit Member"
          meetingTypes={meetingTypes}
          initialData={editingMember}
          onSubmit={(data) => handleEditMember(editingMember.id, data)}
          onClose={() => setEditingMember(null)}
          loading={loading}
        />
      )}
    </div>
  );
}

interface MemberModalProps {
  title: string;
  meetingTypes: any[];
  initialData?: Member | null;
  onSubmit: (data: MemberFormData) => void;
  onClose: () => void;
  loading: boolean;
  defaultMeetingTypeId?: string;
}

function MemberModal({ title, meetingTypes, initialData, onSubmit, onClose, loading, defaultMeetingTypeId }: MemberModalProps) {
  const [formData, setFormData] = useState<MemberFormData>({
    name: initialData?.name || '',
    phone: initialData?.phone || '',
    meeting_type_id: initialData?.meeting_type_id || defaultMeetingTypeId || (meetingTypes[0]?.id || ''),
    tags: initialData?.tags || ''
  });
  const [suggestedMember, setSuggestedMember] = useState<Member | null>(null);

  const handlePhoneChange = async (val: string) => {
    setFormData(prev => ({ ...prev, phone: val }));
    const clean = val.trim().replace(/[^0-9]/g, '');
    if (clean.length >= 5 && !initialData) {
      const { data } = await MemberService.findMemberByPhone(clean);
      if (data && data.length > 0) {
        setSuggestedMember(data[0]);
      } else {
        setSuggestedMember(null);
      }
    } else {
      setSuggestedMember(null);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 transition-opacity">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-md w-full p-4 sm:p-6 space-y-4 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200">
        <div className="w-12 h-1.5 bg-gray-300 rounded-full mx-auto mb-1 sm:hidden shrink-0" />
        <h3 className="text-base sm:text-lg font-bold text-gray-900">{title}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
              Phone Number *
            </label>
            <input
              type="tel"
              required
              value={formData.phone}
              onChange={(e) => handlePhoneChange(e.target.value)}
              placeholder="e.g. 9886101271"
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
            />
          </div>

          {suggestedMember && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-3 text-xs flex items-center justify-between shadow-2xs">
              <div>
                <span className="font-bold text-blue-900 flex items-center space-x-1">
                  <Sparkles size={14} className="text-blue-600" />
                  <span>Existing Member Found:</span>
                </span>
                <span className="text-blue-800 font-semibold">{suggestedMember.name} ({suggestedMember.phone})</span>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFormData(prev => ({
                    ...prev,
                    name: suggestedMember.name,
                    tags: suggestedMember.tags || prev.tags
                  }));
                  setSuggestedMember(null);
                }}
                className="px-2.5 py-1 bg-blue-600 text-white rounded-lg font-bold text-xs hover:bg-blue-700 transition shrink-0 ml-2 shadow-2xs"
              >
                Auto-Fill
              </button>
            </div>
          )}

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
              Member Name *
            </label>
            <input
              type="text"
              required
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g. Abdhulla"
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
            />
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
              Meeting Type *
            </label>
            <select
              value={formData.meeting_type_id}
              onChange={(e) => setFormData(prev => ({ ...prev, meeting_type_id: e.target.value }))}
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
            >
              {meetingTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name} - {type.level}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1">
              Tags (Optional)
            </label>
            <input
              type="text"
              value={formData.tags || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
              placeholder="e.g. Executive, Cabinet"
              className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none text-xs sm:text-sm bg-white"
            />
          </div>

          <div className="flex space-x-3 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 text-xs sm:text-sm font-semibold border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 transition min-h-[44px]"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 text-xs sm:text-sm font-semibold bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition disabled:opacity-50 min-h-[44px] shadow-sm flex items-center justify-center space-x-2"
            >
              {loading ? 'Saving...' : 'Save Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface ImportMembersModalProps {
  meetingTypes: any[];
  currentUserId: string;
  defaultMeetingTypeId?: string;
  onImportSuccess: () => void;
  onClose: () => void;
}

function ImportMembersModal({ meetingTypes, currentUserId, defaultMeetingTypeId, onImportSuccess, onClose }: ImportMembersModalProps) {
  const [targetMeetingTypeId, setTargetMeetingTypeId] = useState(defaultMeetingTypeId || (meetingTypes[0]?.id || ''));
  const [sourceTab, setSourceTab] = useState<'other_meetings' | 'all_system'>('other_meetings');
  const [sourceMembers, setSourceMembers] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');

  const loadSourceMembers = useCallback(async () => {
    setLoading(true);
    setError('');
    let res;
    if (sourceTab === 'other_meetings') {
      res = await MemberService.getConvenerOtherMembers(currentUserId, targetMeetingTypeId);
    } else {
      res = await MemberService.getAllSystemMembers();
    }

    if (res.data) {
      setSourceMembers(res.data);
    } else if (res.error) {
      setError(res.error.message);
    }
    setLoading(false);
  }, [currentUserId, targetMeetingTypeId, sourceTab]);

  useEffect(() => {
    loadSourceMembers();
  }, [loadSourceMembers]);

  const filteredMembers = sourceMembers.filter(m => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      m.name.toLowerCase().includes(q) ||
      m.phone.includes(q) ||
      (m.tags && m.tags.toLowerCase().includes(q))
    );
  });

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredMembers.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMembers.map(m => m.id)));
    }
  };

  const toggleMember = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const handleImport = async () => {
    if (selectedIds.size === 0) return;
    if (!targetMeetingTypeId) {
      setError('Please select a target meeting type');
      return;
    }

    setImporting(true);
    setError('');

    const membersToImport = sourceMembers
      .filter(m => selectedIds.has(m.id))
      .map(m => ({
        name: m.name,
        phone: m.phone,
        tags: m.tags
      }));

    const { error: importError } = await MemberService.bulkAddMembersToMeetingType(
      targetMeetingTypeId,
      membersToImport
    );

    if (importError) {
      setError(importError.message);
    } else {
      await onImportSuccess();
      onClose();
    }
    setImporting(false);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-end sm:items-center justify-center z-50 p-0 sm:p-4 transition-opacity">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[90vh] flex flex-col shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-200">
        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-base sm:text-lg font-bold text-gray-900">Import / Select Existing Members</h3>
            <p className="text-xs text-gray-500">Pick members from other meeting types or system roster without re-typing.</p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg">
            <XCircle size={20} />
          </button>
        </div>

        {/* Target Meeting Type Bar */}
        <div className="px-4 sm:px-6 py-3 bg-blue-50/70 border-b border-blue-100 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs shrink-0">
          <div>
            <label className="block font-bold text-blue-900 mb-1">Add Members To Meeting Type:</label>
            <select
              value={targetMeetingTypeId}
              onChange={(e) => setTargetMeetingTypeId(e.target.value)}
              className="w-full px-3 py-2 border border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-white font-semibold text-gray-900"
            >
              {meetingTypes.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.level})</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block font-bold text-blue-900 mb-1">Select Source Roster:</label>
            <div className="flex bg-white p-1 rounded-xl border border-blue-200">
              <button
                type="button"
                onClick={() => setSourceTab('other_meetings')}
                className={`flex-1 py-1.5 rounded-lg font-bold transition text-xs ${
                  sourceTab === 'other_meetings' ? 'bg-blue-600 text-white shadow-2xs' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                My Other Meetings
              </button>
              <button
                type="button"
                onClick={() => setSourceTab('all_system')}
                className={`flex-1 py-1.5 rounded-lg font-bold transition text-xs ${
                  sourceTab === 'all_system' ? 'bg-blue-600 text-white shadow-2xs' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                All System Members
              </button>
            </div>
          </div>
        </div>

        {/* Search & Actions Bar */}
        <div className="px-4 sm:px-6 py-3 border-b border-gray-100 flex flex-wrap items-center justify-between gap-2 shrink-0">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search member name, phone..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-blue-500 outline-none bg-white"
            />
          </div>

          <div className="flex items-center space-x-2">
            <button
              type="button"
              onClick={toggleSelectAll}
              disabled={filteredMembers.length === 0}
              className="px-3 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 text-xs font-bold rounded-xl transition"
            >
              {selectedIds.size === filteredMembers.length && filteredMembers.length > 0 ? 'Deselect All' : 'Select All'}
            </button>
            <span className="text-xs font-semibold text-blue-700 bg-blue-50 px-2.5 py-1.5 rounded-xl border border-blue-100">
              {selectedIds.size} Selected
            </span>
          </div>
        </div>

        {/* Members Roster List */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-2">
          {loading ? (
            <LoadingState title="Loading available members..." minHeight="min-h-[200px]" />
          ) : error ? (
            <div className="p-4 bg-red-50 text-red-700 rounded-xl text-xs">{error}</div>
          ) : filteredMembers.length === 0 ? (
            <div className="text-center py-10 text-gray-500 text-xs">
              No members found in this roster. Try switching source or search filter.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {filteredMembers.map(m => {
                const isSelected = selectedIds.has(m.id);
                return (
                  <div
                    key={m.id}
                    onClick={() => toggleMember(m.id)}
                    className={`p-3 rounded-xl border cursor-pointer transition flex items-center justify-between ${
                      isSelected
                        ? 'bg-blue-50/80 border-blue-300 shadow-2xs'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500 border-gray-300"
                      />
                      <div className="min-w-0">
                        <p className="font-bold text-gray-900 text-xs truncate">{m.name}</p>
                        <p className="text-[11px] text-gray-500 font-mono">{m.phone}</p>
                        {m.meeting_types?.name && (
                          <span className="inline-block px-1.5 py-0.2 bg-gray-100 text-gray-600 rounded text-[10px] font-medium mt-0.5">
                            {m.meeting_types.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 sm:px-6 py-3.5 border-t border-gray-200 bg-gray-50 flex items-center justify-between shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200 rounded-xl transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleImport}
            disabled={importing || selectedIds.size === 0}
            className="px-5 py-2.5 bg-blue-600 text-white text-xs sm:text-sm font-bold rounded-xl hover:bg-blue-700 transition disabled:opacity-50 shadow-md flex items-center space-x-1.5"
          >
            <UserPlus size={16} />
            <span>{importing ? 'Importing...' : `Import (${selectedIds.size}) Members`}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
